import React, { useEffect, useState, useMemo, useRef } from "react"
import { getHierarchyConditionsRequest, readAllowedIds } from "Utils/hierarchyConditions"
import PropTypes from "prop-types"
import { withRouter } from "HOC/withRouter"
import { connect } from "react-redux"
import { compose } from "redux"
import { createStructuredSelector } from "reselect"

import { getTranslation } from "Utils/getTranslation"
import { specLibelle } from "Utils/optionLabel"
import {
  isFilterWidget,
  isWidgetChart,
  getWidgetComponent,
  authorizedWidgetTypeToCompare,
  filtreCategorieFusionnee,
} from "Utils/widgetsUtils"
import { getPeriodRangeFilters } from "Utils/periodDrillDown"
import { corpsRecence, valeurLaPlusRecente } from "Utils/mostRecentFilterDefault"
import { config } from "Utils/config"
import {
  construireColonnesDrillDown,
  cibleDrillDown,
  cibleNavigationControle,
  TYPES_AVEC_DRILL_DOWN,
  estColonneTechnique,
} from "Utils/drillDownColumns"

import Button from "Components/Button"
import { VALEUR_VIDE } from "Components/chartsLibrary/DropDownList"
import WidgetLoader from "Components/WidgetLoader"
import ErrorBoundary from "Components/ErrorBoundary"
import ErrorWidget from "Images/nocontent/ErrorWidget"

// SVG
import IconFullScreenOn from "Images/icon/icon_fullscreen_on"

//SELECTORS
import {
  selectPrimaryActionColor,
  selectBusinessId,
} from "Containers/BusinessInformations/selectors"

import { selectCurrentToken, selectLangageID } from "Containers/Login/selectors"

import {
  makeGetWidgetDataWithId,
  makeGetWidgetFilterDataWithId,
  makeGetWidgetRequestStatusWithId,
  selectWidgetFilterData,
  selectComparisonValues,
  selectDrillDownEnabled,
  selectDependentFilters,
} from "Containers/Page/selectors"

import { getFields } from "Shared/hooks/useDashboardData"

export function getPageIdFromPathname(pathname) {
  const m = pathname.match(/^\/page\/([a-f0-9]{24})\/?$/i)
  return m ? m[1] : undefined
}

// Le widget hierarchicalFilter émet des __id de samples (node.id = __id du référentiel).
// data[0].id est la clé de schéma du champ métier choisi : sa colonne du cube contient des
// valeurs métier, PAS des __id → filtrer dessus ne matche jamais. Il faut cibler la colonne
// idSample du cube (qui, elle, contient le __id) :
//   - racine du cube   → "idSample"
//   - référentiel lié  → "<prefixe>.idSample" (ex. "loaded_left_<relId>.idSample")
// Miroir exact de la logique back (bi/services/sql-builder.service.ts:346-352,411-416).
export function resolveIdSampleColumn(fieldKey) {
  if (typeof fieldKey === "string" && fieldKey.startsWith("loaded")) {
    const parts = fieldKey.split(".")
    parts.pop()
    return `${parts.join(".")}.idSample`
  }
  return "idSample"
}

//ACTIONS
import {
  selectComparisonValueAction,
  removeComparisonValueAction,
  getWidgetDataAction,
  setWidgetRequestStatus,
} from "Containers/Page/actions"

import {
  getWidgetTraduction,
  widgetCategory,
  isEmptyWidget,
  isTextWidget,
  generateETLRequestJsonFromWidgetInfo,
  getCorrectRelatedPageFilter,
  isWidgetNeedToReload,
  getDataRelatedProps,
  getNewWidgetPropsToInjectInComp,
  getComparedWidget,
} from "Utils/widgetsUtils"

import { useDidMountEffect } from "Utils/customHook.js"
import { cleRequeteWidget, creerFiltreRequetesIdentiques } from "Utils/requeteWidgetUnique"
import { getPageImageUrl } from "Utils/useImage"
import { filterStore } from "Shared/stores/filterStore"
import { useFilterStore } from "Shared/hooks/useFilterStore"
import DrillDownModal from "Shared/components/DrillDownModal"
import WidgetDescription from "Containers/Widget/WidgetDescription"

import {
  WidgetContainer,
  WidgetHeader,
  ButtonSettings,
  Title,
  ChartContainer,
  ConfigurationContainer,
  EmptyWidgetContainer,
  ErrorWidgetContainer,
} from "./Widget.styles"

/**
 * Opération ETL « la valeur figure PARMI les éléments de la cellule », pour les champs dont une
 * cellule porte plusieurs valeurs jointes. Côté serveur : `convertFilterToSQL`.
 */
const OPERATION_MULTIVALUEE = "CONTAINS_ITEM"

function Widget({
  id,
  languageId,
  widgetInfo,
  primaryActionColor,
  onWidgetConfigClick,
  onWidgetFullScreenClick,
  showWidgetConfigButton,
  layout,
  currentColsNumber,
  isAWidgetFromSettings,
  isWidgetFromFullScreen,
  sideMenuColor,
  breakpoint,
  //REDUX
  specificWidgetData,
  specificWidgetFilterData,
  specificWidgetRequestStatus,
  valueFilterSelected,
  comparisonValues,
  drillDownEnabled,
  dependentFilters,
  businessId,
  token,
  //Action
  onGetWidgetData,
  onSelectComparisonValue,
  onRemoveComparisonValue,
  onSelectFilterValue,
  onRemoveSelectedValue,
  setRequestStatus,
  updateDataSource,
  savePipelineModifications,
}) {
  const { filters: eventFilters } = useFilterStore()
  const trad = useMemo(() => getTranslation(languageId), [languageId])
  const partialTrad = {
    DAY: trad.DAY,
    MONTH: trad.MONTH,
    YEAR: trad.YEAR,
  }
  const widgetIsComparedFunc = (comparisonValues, widgetInfo, isAWidgetFromSettings) => {
    if (isAWidgetFromSettings) return false
    return (
      comparisonValues &&
      Object.keys(comparisonValues).length > 0 &&
      authorizedWidgetTypeToCompare.includes(widgetInfo.widgetType)
    )
  }

  const idPage = getPageIdFromPathname(location?.pathname)
  const { fields: fieldsData, referentialId: dashboardReferentialId } = getFields(idPage, token) // Si besoin de données spécifiques au dashboard

  const [valueData, setValueData] = useState("")
  const [categoryData, setCategoryData] = useState("")
  const [comparedWidget, setComparedWidget] = useState({})
  const [isLoadingWidget, setIsLoadingWidget] = useState(true)
  const requeteDejaDemandee = useRef(creerFiltreRequetesIdentiques()).current
  const [isDataLoaderVisible, setDataLoaderVisible] = useState(false)
  const [isDataLoaded, setDataLoaded] = useState(false)
  const [drillDownState, setDrillDownState] = useState({ open: false, referentialId: "", dashboardId: "", etlBody: null, fieldsData: [], idSampleKey: "idSample", preloadedItems: null, preloadedColumns: null })

  useEffect(() => {
    if (widgetInfo && !isAWidgetFromSettings) {
      getWidgetData(widgetInfo, eventFilters, fieldsData, "dataChange", comparisonValues)
    }

    const isNotACalendar = widgetInfo?.widgetType !== "calendar"
    if (!isEmptyWidget(widgetInfo) && !isTextWidget(widgetInfo) && isNotACalendar)
      setIsLoadingWidget(true)
    else setIsLoadingWidget(false)
  }, [])

  const widgetInfoStableKey = widgetInfo ? `${widgetInfo._id?.$oid}_${JSON.stringify(widgetInfo.data)}_${JSON.stringify(widgetInfo.groupBy)}` : null

  useEffect(() => {
    if (!widgetInfo) return
    const fieldsReady = fieldsData && Object.keys(fieldsData || {}).length > 0
    const filtersReady = eventFilters && Object.keys(eventFilters).length >= 0
    if (fieldsReady && filtersReady) {
      getWidgetData(widgetInfo, eventFilters, fieldsData, "filterChange", comparisonValues)
    }
  }, [widgetInfoStableKey, isAWidgetFromSettings, fieldsData, eventFilters, valueFilterSelected, comparisonValues])

  const getWidgetData = (
    widgetInfo,
    valueFilterSelected,
    fieldsData,
    dataChangeType,
    comparisonValues
  ) => {
    var widgetInfosToRequest = widgetInfo
    const { data, groupBy, widgetType } = widgetInfo
    if (!isWidgetNeedToReload(widgetInfo, dataChangeType)) return

    const isPipeline = widgetType === "pipeline"
    if (isPipeline && (!data.length || !groupBy.length)) return

    if (!isAWidgetFromSettings && authorizedWidgetTypeToCompare.includes(widgetType)) {
      if (comparisonValues && Object.keys(comparisonValues).length >= 1) {
        widgetInfosToRequest = getComparedWidget(widgetInfo, comparisonValues, fieldsData)
      }
      setComparedWidget(widgetInfosToRequest)
    }

    // `dependentFilters` : quand le réglage de page est actif, une liste déroulante restreint ses
    // options d'après les autres filtres (écart E4). Sans lui, elle les ignore — comportement
    // d'origine, et celui de tous les dashboards qui ne portent pas le réglage.
    var pageFilterAuthorised = getCorrectRelatedPageFilter(
      widgetInfosToRequest,
      valueFilterSelected,
      fieldsData,
      dependentFilters
    )

    var dataRequest = generateETLRequestJsonFromWidgetInfo(
      widgetInfosToRequest,
      pageFilterAuthorised
    )

    const getPageIdWithPathName = pathname => {
      var splittedPath = pathname.split("/")
      return splittedPath.pop()
    }

    /**
     * Correspondance `outputUUID` → `fieldUUID` des colonnes demandées.
     *
     * Le moteur rend chaque colonne sous son `valueId` (tiré au hasard à la construction du
     * widget), alors qu'un filtre doit nommer un `fieldUUID`. Seul cet endroit connaît les deux :
     * la saga ne reçoit que des lignes, et le widget ne reçoit que des options.
     */
    const outputToField = {}
    ;(widgetInfosToRequest?.data ?? []).forEach(f => {
      const sortie = f?.valueId ?? f?.id
      const champ = f?.uuid ?? f?.id
      if (sortie && champ) outputToField[sortie] = champ
    })

    // Deux effets demandent la même requête dans le même cycle : elle ne part qu'une fois
    // (`Utils/requeteWidgetUnique`).
    if (requeteDejaDemandee(cleRequeteWidget(widgetInfosToRequest._id.$oid, dataRequest))) return

    onGetWidgetData({
      widgetType: widgetInfosToRequest?.widgetType,
      multiValueSeparator: widgetInfosToRequest?.widgetProps?.multiValueSeparator ?? null,
      outputToField,
      // Libellé combiné des options (`Utils/optionLabel`) : `null` sans réglage.
      optionLabel: specLibelle(widgetInfosToRequest),
      widgetId: widgetInfosToRequest._id.$oid,
      dataCatalogueName: "defaultDataCatalogue",
      dashboardId: getPageIdWithPathName(location?.pathname),
      body: dataRequest,
    })
  }

  //not need to get data on mount because it's get while getting page
  useDidMountEffect(() => {
    getWidgetData(widgetInfo, eventFilters, fieldsData, "dataChange", comparisonValues)
  }, [widgetInfo?.groupBy, widgetInfo?.data, widgetInfo?.dependsOnField])

  // Libellé combiné des options : réglé dans `widgetProps`, que l'effet ci-dessus ne surveille pas.
  // Sans celui-ci, changer les champs affichés dans la configuration n'aurait aucun effet visible
  // avant un rechargement. Dépend de la FORME sérialisée : l'objet est recréé à chaque rendu.
  useDidMountEffect(() => {
    getWidgetData(widgetInfo, eventFilters, fieldsData, "dataChange", comparisonValues)
  }, [JSON.stringify(widgetInfo?.widgetProps?.optionLabel ?? null)])

  useDidMountEffect(() => {
    getWidgetData(widgetInfo, eventFilters, fieldsData, "filterChange", comparisonValues)
  }, [eventFilters, fieldsData])

  useDidMountEffect(() => {
    getWidgetData(widgetInfo, eventFilters, fieldsData, "comparisonChange", comparisonValues)
  }, [comparisonValues])

  const onWidgetPropsChange = widgetInformations => {
    if (
      !widgetInformations ||
      !isWidgetChart(widgetInformations) ||
      !Boolean(specificWidgetData?.columns?.length)
    )
      return

    const { widgetType } = widgetInformations
    var columnsUUIDList = specificWidgetData.columns.map(col => col.uuid)

    var listOfDataValueID = widgetInformations.data.map(valueObject => {
      return valueObject?.valueId || valueObject.id
    })

    const isTable = widgetType === "table"

    if (isTable && Array.isArray(widgetInformations.groupBy)) {
      var tempDataIDList = []
      widgetInformations.groupBy.forEach(dataUUIDObject =>
        tempDataIDList.push(dataUUIDObject?.valueId || dataUUIDObject.id)
      )
      //the order here is important
      var temporaryDataWanted = [...tempDataIDList, ...listOfDataValueID]
      var tempSet = new Set(temporaryDataWanted)
      listOfDataValueID = [...tempSet]
    }

    var dataKeys = getDataKeys(listOfDataValueID, columnsUUIDList, widgetType)
    setValueData(dataKeys)
    // const categoryId = widgetInformations?.category?.id ? widgetInformations.category.id : ""
    var categoryId = ""
    if (widgetInformations?.groupBy?.[0]?.valueId)
      categoryId = widgetInformations.groupBy[0].valueId
    else if (widgetInformations?.groupBy?.[0]?.uuid) categoryId = widgetInformations.groupBy[0].uuid
    setCategoryData(categoryId)
  }

  /**
   * VALEUR PAR DÉFAUT DYNAMIQUE d'un filtre — « le plan de contrôle le plus récent » (écart E1).
   *
   * Le mécanisme statique existe déjà (`defaultValues`, posé par `getInitialPageFilter`), mais il
   * porte des valeurs LITTÉRALES : figée à l'installation, la valeur désignerait encore l'ancien
   * plan le jour où un nouveau démarre, sans que rien ne le signale. Le widget déclare donc le
   * champ de DATE qui porte la récence, et la valeur se recalcule à chaque ouverture.
   *
   * ⚠️ TROIS GARDES, et chacune ferme un défaut précis :
   *
   *  1. `wasRestored()` — un défaut dynamique se pose à l'arrivée des données, donc APRÈS la
   *     restauration des filtres mémorisés. Sans cette garde il réécrirait un filtre que
   *     l'utilisateur venait délibérément de vider, ce que la règle des valeurs par défaut interdit
   *     partout ailleurs.
   *  2. une valeur déjà posée n'est jamais écrasée — sinon le défaut reviendrait à chaque
   *     rechargement de données, et l'utilisateur ne pourrait pas l'enlever. Or le client demande
   *     explicitement « avec la possibilité de l'enlever ».
   *  3. une seule tentative par widget (`ref`) — la requête ne doit pas se rejouer à chaque rendu.
   *
   * Rendre la main sans rien poser est un cas NORMAL : filtre vide, comme avant.
   */
  const defautTenteRef = useRef(new Set())

  useEffect(() => {
    const props = widgetInfo?.widgetProps
    const dateUUID = props?.defaultToMostRecentByFieldUUID
    const widgetId = widgetInfo?._id?.$oid
    const champUUID = widgetInfo?.data?.[0]?.uuid || widgetInfo?.data?.[0]?.id

    if (!dateUUID || !champUUID || !widgetId || isAWidgetFromSettings) return
    if (defautTenteRef.current.has(widgetId)) return
    if (filterStore.wasRestored()) return
    if (filterStore.get(widgetId)) return

    defautTenteRef.current.add(widgetId)

    if (!idPage) return

    const cleValeur = "v"
    const cleDate = "d"
    let annule = false

    fetch(`${config.APOLLO_SERVER_URL}/etl/dashboard/${idPage}/request`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(corpsRecence(champUUID, dateUUID, cleValeur, cleDate)),
    })
      .then(r => (r.ok ? r.json() : null))
      .then(reponse => {
        if (annule || !reponse) return
        const lignes = reponse?.body?.data ?? reponse?.data ?? []
        const valeur = valeurLaPlusRecente(lignes, cleValeur, cleDate)
        if (valeur === null) return
        // Les gardes sont revérifiées : la requête est asynchrone, l'utilisateur a pu choisir
        // entre-temps — et écraser son choix serait le défaut que la garde 2 interdit.
        if (filterStore.wasRestored() || filterStore.get(widgetId)) return
        filterStore.set(widgetId, {
          value: [valeur],
          fieldUUID: champUUID,
          operation: widgetInfo?.widgetProps?.multiValueSeparator ? OPERATION_MULTIVALUEE : "==",
        })
      })
      .catch(() => {
        // Silence volontaire : un défaut qu'on n'a pas su calculer laisse le filtre vide, ce qui
        // est le comportement d'avant. Rien ne justifie d'alerter l'utilisateur.
      })

    return () => {
      annule = true
    }
  }, [widgetInfo, isAWidgetFromSettings, token, idPage])

  /**
   * Filtre hiérarchique : fiches qui respectent les CONDITIONS du widget (cf. `hierarchyConditions.js`).
   * `null` = pas de condition (ou pas encore de réponse) → arbre complet, comme avant.
   *
   * En cas d'échec de la requête, l'arbre reste complet : les conditions RESTREIGNENT une liste de
   * choix déjà filtrée par les droits côté serveur, elles ne sont pas une porte d'accès.
   */
  const [hierarchyAllowedIds, setHierarchyAllowedIds] = useState(null)
  const hierarchyRequest = getHierarchyConditionsRequest(widgetInfo, resolveIdSampleColumn)
  // Clé stable : l'effet ne se rejoue que si les conditions ou le champ changent vraiment.
  const hierarchyRequestKey = hierarchyRequest ? JSON.stringify(hierarchyRequest) : ""

  useEffect(() => {
    if (!hierarchyRequestKey || !idPage) {
      setHierarchyAllowedIds(null)
      return
    }
    let annule = false
    fetch(`${config.APOLLO_SERVER_URL}/etl/dashboard/${idPage}/request`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: hierarchyRequestKey,
    })
      .then(r => (r.ok ? r.json() : null))
      .then(reponse => {
        if (annule) return
        setHierarchyAllowedIds(reponse ? readAllowedIds(reponse) : null)
      })
      .catch(error => {
        if (annule) return
        // eslint-disable-next-line no-console
        console.warn("[filtre hiérarchique] conditions non appliquées — arbre complet", error)
        setHierarchyAllowedIds(null)
      })
    return () => {
      annule = true
    }
  }, [hierarchyRequestKey, idPage, token])

  const isDataAvailable = (availableColumn, columnId) => {
    return availableColumn.includes(columnId)
  }

  const getDataKeys = (listOfDataValueID, availableColumn, widgetType) => {
    var dataKeys = []
    listOfDataValueID.forEach(columnId => {
      isDataAvailable(availableColumn, columnId) && dataKeys.push(columnId)
    })
    if (widgetType === "pieChart" && dataKeys.length > 2) dataKeys.splice(2)
    return dataKeys
  }

  useEffect(() => {
    var widgetIsCompared = widgetIsComparedFunc(comparisonValues, widgetInfo, isAWidgetFromSettings)
    if (widgetIsCompared) {
      var comparedWidgetToUse = getComparedWidget(widgetInfo, comparisonValues, fieldsData)
      setComparedWidget(comparedWidgetToUse)
      onWidgetPropsChange(comparedWidgetToUse)
    } else if (widgetInfo) {
      onWidgetPropsChange(widgetInfo)
    }
    // génére un lien vers l'image si dispo
    const widgetId = widgetInfo?._id?.$oid
    if (
      widgetInfo?.widgetType === "image" &&
      widgetInfo?.isUploadedImage &&
      widgetId !== "widgetBeingEdited"
    ) {
      if (!widgetInfo.hasOwnProperty("temporaryId")) {
        getPageImageUrl(businessId, widgetId, undefined, token, "widgets").then(url => {
          widgetInfo.widgetProps["urlImage"] = url
        })
      } else if (widgetInfo.widgetProps?.tempUrlImage === "") {
        getPageImageUrl(businessId, widgetId, undefined, token, "workingWidgets").then(url => {
          widgetInfo.widgetProps["tempUrlImage"] = url
        })
      }
    }
  }, [widgetInfo, specificWidgetData])

  useEffect(() => {
    if (specificWidgetRequestStatus === "PENDING") {
      if (isLoadingWidget) {
        setDataLoaderVisible(false)
        setDataLoaded(false)
      } else {
        setDataLoaderVisible(true)
        setDataLoaded(false)
      }
    } else if (specificWidgetRequestStatus === "SUCCESS") {
      if (isLoadingWidget) {
        setIsLoadingWidget(false)
      } else setDataLoaded(true)
    } else if (specificWidgetRequestStatus === "ERROR") setDataLoaderVisible(false)

    if (isTextWidget(widgetInfo)) {
      setDataLoaderVisible(false)
      setDataLoaded(false)
    }
  }, [specificWidgetRequestStatus])

  useDidMountEffect(() => {
    if (isDataLoaded && isDataLoaderVisible) {
      removeDataLoader()
    } else if (isDataLoaded && !isDataLoaderVisible) {
      setDataLoaded(false)
    }
  }, [isDataLoaded])

  const removeDataLoader = () => {
    setTimeout(() => {
      setDataLoaderVisible(false)
      setDataLoaded(false)
    }, 7000)
  }

  const onSelectFilterValueWrapperFunc = (valueLabelSelected, widgetId) => {
    const { widgetType } = widgetInfo
    // Un widget qui déclare un séparateur porte plusieurs valeurs par cellule : l'égalité ne
    // désignerait que la COMBINAISON entière, donc zéro ligne pour une personne choisie seule.
    // Le même drapeau pilote l'éclatement des options : impossible d'en poser un sans l'autre.
    let operation = widgetInfo?.widgetProps?.multiValueSeparator ? OPERATION_MULTIVALUEE : "=="
    if (widgetInfo.data.length && widgetInfo.data[0].id) {
      var fieldUUID = widgetInfo.data[0].id
      // hierarchicalFilter : les valeurs émises sont des __id de samples → cibler la colonne
      // idSample (racine ou référentiel lié), pas le champ métier data[0].id (0 résultat sinon).
      if (widgetType === "hierarchicalFilter") {
        fieldUUID = resolveIdSampleColumn(widgetInfo.data[0].id)
      }
      //idea : depending on valueLabelSelected?.value, if undefined, call action
      // to remove SelectedFilterValue.
      var filterValue, rangeValue
      if (["calendar", "rangeFilter"].includes(widgetType)) {
        filterValue = valueLabelSelected.value
        operation = valueLabelSelected.operation
        if (
          widgetType === "rangeFilter" &&
          widgetInfo?.groupBy.length &&
          widgetInfo?.groupBy[0]?.id
        ) {
          fieldUUID = widgetInfo.groupBy[0].id
          rangeValue = valueLabelSelected.rangeSelected
        }
      } else {
        if (Array.isArray(valueLabelSelected)) {
          if (valueLabelSelected.length) {
            filterValue = valueLabelSelected.map(objValueLabel => objValueLabel.value)
          } else {
            filterValue = null
          }
        } else {
          filterValue = valueLabelSelected?.value ?? null
        }
      }

      /**
       * BARRE DE RECHERCHE MULTI-CHAMPS : un OU entre plusieurs colonnes.
       *
       * `fieldUUID`/`value` ne savent désigner qu'UNE colonne. Une barre qui cherche dans le code,
       * le libellé et les personnes émet donc `byField` — une liste de valeurs par champ — que ce
       * gestionnaire ignorait purement et simplement : `valueLabelSelected.value` valant
       * `undefined` sur cet objet, il enregistrait `value: null` sur le premier champ, et le filtre
       * ne faisait rien. Troisième maillon rompu de la même chaîne, après la requête et l'indexation
       * des options.
       *
       * La forme produite ici — `conditions` — est celle que `getTransformedFilterFromPage` sait
       * déjà transformer en bloc `or`. Elle existait, elle n'était jamais alimentée.
       *
       * ⚠️ `fieldUUID` et `value` restent renseignés, sur le premier champ : plusieurs chemins les
       * lisent sans connaître `conditions` (l'affichage de la valeur choisie, la garde de filtre
       * vide). Les laisser vides aurait fait disparaître le filtre de l'écran.
       */
      /**
       * Option « Vide » — traduite en `IS NULL`, jamais envoyée telle quelle.
       *
       * La sentinelle sort de `DropDownList` ; c'est ici qu'elle redevient une intention. Le cas
       * MIXTE — « Oui » ET « Vide » — se règle par le même tableau `conditions` que la recherche
       * multi-champs : deux entrées dans un OU, l'une sur les valeurs réelles, l'autre sur
       * l'absence de valeur. Aucun mécanisme nouveau.
       */
      const valeursBrutes = Array.isArray(valueLabelSelected)
        ? valueLabelSelected.map(v => v?.value)
        : [valueLabelSelected?.value]
      const videChoisi = valeursBrutes.includes(VALEUR_VIDE)
      const valeursReelles = valeursBrutes.filter(v => v !== VALEUR_VIDE && v !== undefined && v !== null)

      const byField = !Array.isArray(valueLabelSelected) ? valueLabelSelected?.byField : null
      const conditions =
        byField && Object.keys(byField).length
          ? Object.entries(byField).map(([champ, valeurs]) => ({
              fieldUUID: champ,
              operation,
              value: valeurs,
            }))
          : videChoisi
            ? [
                ...(valeursReelles.length
                  ? [{ fieldUUID, operation, value: valeursReelles }]
                  : []),
                { fieldUUID, operation: "IS NULL" },
              ]
            : null

      var payloadToSend = {
        id: widgetId,
        /**
         * ⚠️ `value` reste renseignée pour les chemins qui ne connaissent pas `conditions` —
         * l'affichage de la sélection, la garde de filtre vide.
         *
         * ⚠️ **Quand « Vide » est choisi, on enregistre la SÉLECTION ENTIÈRE, sentinelle comprise**,
         * et non la seule valeur de la première condition. C'est `value` qui redescend dans la liste
         * pour en cocher les options : n'y garder que les valeurs réelles faisait disparaître la
         * pastille « Vide » d'une sélection pourtant APPLIQUÉE (cas mixte « Oui » + « Vide »), et
         * n'y garder que la sentinelle scalaire la faisait afficher en clair —
         * `getDataRelatedProps` réemballe une valeur non-tableau en tableau d'un élément.
         * La sentinelle n'atteint toujours pas le moteur : `conditions` porte l'intention réelle.
         */
        value: conditions
          ? videChoisi
            ? valeursBrutes
            : conditions[0].value ?? filterValue
          : filterValue,
        fieldUUID: conditions ? conditions[0].fieldUUID : fieldUUID,
        operation,
        ...(conditions && { conditions }),
        ...(rangeValue && { rangeValue: rangeValue }),
      }
      filterStore.set(payloadToSend.id, {
        value: payloadToSend.value,
        fieldUUID: payloadToSend.fieldUUID,
        operation: payloadToSend.operation,
        ...(payloadToSend.conditions && { conditions: payloadToSend.conditions }),
        rangeValue: payloadToSend.rangeValue,
      })
    } else {
      console.error("ERROR on filter selection")
    }
  }

  const onSelectComparisonValueWrapperFunc = (valueLabelSelected, widgetId) => {
    let operation = "=="
    if (widgetInfo.data.length && widgetInfo.data[0].id) {
      var fieldUUID = widgetInfo.data[0].id

      var { firstValue, secondValue } = valueLabelSelected
      operation = valueLabelSelected.operation

      var payloadToSend = {
        widgetId: widgetId,
        firstValue: firstValue,
        secondValue: secondValue,
        fieldUUID: fieldUUID,
        operation,
      }

      onSelectComparisonValue(payloadToSend)
    } else {
      console.error("ERROR on comparison selection")
    }
  }

  const onRemoveSelectedValueFunc = widgetId => {
    filterStore.remove(widgetId)
  }

  // Aplatit l'arbre de conditions du widget en filterModel AG Grid
  const extractConditionsToFilterModel = (conditions, model) => {
    if (!conditions?.filters?.length) return
    conditions.filters.forEach(filter => {
      if (filter.fieldUUID && filter.value !== undefined && filter.value !== null && filter.value !== "") {
        const agType = filter.operation === "!=" ? "notEqual" : "equals"
        model[filter.fieldUUID] = {
          filterType: Array.isArray(filter.value) ? "set" : "text",
          type: agType,
          ...(Array.isArray(filter.value) ? { values: filter.value } : { filter: String(filter.value) }),
        }
      } else if (filter.filters) {
        extractConditionsToFilterModel(filter, model)
      }
    })
  }

  const handleDrillDown = ({ categoryValue, seriesIndex, preloadedItems, referentialId: overrideReferentialId }) => {
    // Cas heatmap : données pré-chargées, pas besoin de construire un ETL
    if (preloadedItems) {
      // Récupérer les noms de champs depuis widgetInfo.data + fieldsData
      const xField = widgetInfo?.data?.find(f => f.category === "x")
      const yField = widgetInfo?.data?.find(f => f.category === "y")
      const nameField = widgetInfo?.data?.find(f => f.category === "name")
      const computedColumns = [
        { uuid: "name", name: nameField?.name || "Nom", dataKey: "name" },
        { uuid: "axeX", name: xField?.name || "Axe X", dataKey: "axeX" },
        { uuid: "axeY", name: yField?.name || "Axe Y", dataKey: "axeY" },
        { uuid: "niveau", name: "Niveau", dataKey: "niveau" },
      ]
      setDrillDownState({
        open: true,
        referentialId: overrideReferentialId || dashboardReferentialId || "",
        dashboardId: "",
        etlBody: null,
        fieldsData: [],
        idSampleKey: "idSample",
        preloadedItems,
        preloadedColumns: computedColumns,
      })
      return
    }
    const isGantt = widgetInfo?.widgetType === "ganttChartV2"

    // La cible du drill-down est déterminée par la "donnée effectivement affichée" (AFFICHER = data[0]),
    // pas par le GROUPER PAR. Exemple : AFFICHER=Lib PDC + GROUPER PAR=Id Contrôle → cible = PDC.
    // Le filtre segment (groupBy) s'applique toujours, mais le référentiel et les colonnes suivent
    // le champ affiché.
    const displayFieldUUID = widgetInfo?.data?.[0]?.uuid || widgetInfo?.data?.[0]?.id
    const displayField = fieldsData?.find(f => f.uuid === displayFieldUUID)
    // Detect whether the displayed field comes from a linked referential (loaded_* join).
    // In that case the drill-down must target that referential, not the dashboard's own.
    // Gantt : même logique que les autres widgets — si le champ affiché est un loaded_*
    // (ex. dashboard PDC affichant des Contrôles), le DrillDown ouvre la fiche Contrôle.
    // ⚠️ `widgetProps.drillDownOnRoot` force la cible sur le référentiel du dashboard (voir
    // `cibleDrillDown`) : le widget compte des fiches jointes, mais liste les fiches racines.
    const drillSurRacine = widgetInfo?.widgetProps?.drillDownOnRoot === true
    const { referentialId, isLinkedField } = cibleDrillDown(
      displayField,
      dashboardReferentialId,
      drillSurRacine
    )
    if (!referentialId) return
    // "loaded_left_abc123.fieldId" → prefix = "loaded_left_abc123"
    const linkedPrefix = isLinkedField
      ? String(displayField.uuid).split(".").slice(0, -1).join(".")
      : null
    const linkedIdSampleKey = linkedPrefix ? `${linkedPrefix}.idSample` : "idSample"

    // Build the ETL body reusing the same filters as the chart (page filters + widget conditions)
    const pageFilterAuthorised = getCorrectRelatedPageFilter(widgetInfo, eventFilters, fieldsData)
    const chartBody = generateETLRequestJsonFromWidgetInfo(widgetInfo, pageFilterAuthorised)

    // Construit un filtre feuille par champ groupBy.
    // - Compteur (categoryValue undefined/null) : pas de filtre segment.
    // - Single groupBy : categoryValue est directement la valeur du champ.
    // - Multi groupBy (BarChart) : categoryValue est une combinedCategory "val1 / val2 / …"
    //   (cf. BarChart prepareChartData). On retrouve la ligne originale dans specificWidgetData
    //   pour extraire les valeurs individuelles plutôt que de parser la chaîne concaténée.
    // - Valeur vide ("") : queryPostgres normalise NULL→"" dans les résultats mais PG stocke NULL.
    //   On envoie IS NULL car NULL = '' est faux en SQL.
    const groupByFields = widgetInfo?.groupBy ?? []

    // CORRESPONDANCE (valueMapper) : le chart affiche un label renommé (ex: "NC") mais la DB
    // stocke la valeur d'origine (ex: ""). Le drill doit filtrer sur la valeur d'origine.
    // On reverse-mappe categoryValue → originalValue avant de construire les filtres.
    const valueMapper = widgetInfo?.widgetProps?.valueMapper ?? []
    const reverseMapCategory = val => {
      if (val == null) return val
      const entry = valueMapper.find(m => m.renamedValue === String(val))
      return entry !== undefined ? entry.originalValue : String(val)
    }

    const leafFilters = (() => {
      // Gantt : categoryValue = idSample du sample cliqué (racine ou lié selon la config).
      // À vérifier EN PREMIER, avant le guard groupByFields : un widget Gantt n'a pas de
      // groupBy, donc le guard court-circuiterait le filtre si la vérification venait après.
      // Le fieldUUID suit la même règle que les autres widgets : lié → linked.idSample,
      // direct → idSample.
      if (isGantt) {
        if (categoryValue == null) return []
        const ganttIdSampleUUID = isLinkedField ? linkedIdSampleKey : "idSample"
        return [{ fieldUUID: ganttIdSampleUUID, operation: "==", value: String(categoryValue) }]
      }
      if (groupByFields.length === 0) return []
      // Compteur : onSegmentClick({}) passe categoryValue=undefined → pas de filtre segment
      if (categoryValue === undefined || categoryValue === null) return []

      if (groupByFields.length === 1) {
        const fieldUUID = groupByFields[0].uuid || groupByFields[0].id

        // AXE TEMPOREL groupé par période (courbe mensuelle, histogramme trimestriel…).
        //
        // ⚠️ La catégorie affichée est un LIBELLÉ de période (« 02/2026 »), pas une date : un `==`
        // sur la colonne ne matcherait jamais rien, et le drill ouvrirait une liste vide sans que
        // rien ne signale l'erreur. On la traduit donc en INTERVALLE.
        //
        // Les bornes viennent de la valeur BRUTE de la ligne — que le back a déjà tronquée au
        // début du compartiment — et jamais d'une relecture du libellé, qui est localisé.
        const intervalle = getPeriodRangeFilters({
          groupByField: groupByFields[0],
          fieldUUID,
          categoryValue,
          rows: specificWidgetData?.data,
          fieldsData,
          trad: partialTrad,
          languageId,
        })
        if (intervalle) return intervalle

        // Barre FUSIONNÉE (`mergeRenamedCategories`) : le détail liste TOUTES les valeurs d'origine.
        const filtreFusion = filtreCategorieFusionnee(widgetInfo?.widgetProps, categoryValue, fieldUUID)
        if (filtreFusion) return [filtreFusion]

        const val = reverseMapCategory(categoryValue)
        return [val !== ""
          ? { fieldUUID, operation: "==", value: val }
          : { fieldUUID, operation: "IS NULL" }]
      }

      // Multi-groupBy : retrouver la ligne matchant la combinedCategory
      const groupByOutputUUIDs = groupByFields.map(gb => gb.valueId || gb.uuid)
      const matchRow = (specificWidgetData?.data ?? []).find(row => {
        const combined = groupByOutputUUIDs
          .map(uuid => { const v = row[uuid]; return v != null ? String(v) : "" })
          .join(" / ")
          .trim()
        return (combined !== "" ? combined : "Sans valeur") === String(categoryValue)
      })
      if (!matchRow) return []

      return groupByFields.map(gb => {
        const fieldUUID = gb.uuid || gb.id
        const rawVal = matchRow[gb.valueId || gb.uuid]
        const val = rawVal != null ? String(rawVal) : ""
        return val !== ""
          ? { fieldUUID, operation: "==", value: val }
          : { fieldUUID, operation: "IS NULL" }
      })
    })()

    // Conditions par champ (select.filters) : appliquées en CASE WHEN dans le chart (ex: COUNT
    // de Libellé WHERE Statut = "Actif") mais absentes du drill-down. On les extrait pour les
    // ajouter en filtre WHERE afin que les lignes affichées correspondent au périmètre compté.
    //
    // MODE AVANCÉ multi-séries : chaque série a ses propres conditions. On n'applique que celles
    // de la série cliquée (seriesIndex transmis par BarChart/PieChart). Combiner toutes les séries
    // en AND produirait une condition impossible (ex: Statut=Brouillon ET Statut≠Brouillon → 0).
    const selectAdvanced = chartBody.selectAdvanced ?? []
    const clickedSeriesFilters = (() => {
      if (seriesIndex == null || selectAdvanced.length <= 1) {
        // mono-série ou seriesIndex absent : comportement classique (toutes les conditions)
        return selectAdvanced.map(sel => sel.filters).filter(f => f && Object.keys(f).length > 0)
      }
      // multi-séries : uniquement la série cliquée
      const sel = selectAdvanced[seriesIndex]
      if (!sel?.filters || Object.keys(sel.filters).length === 0) return []
      return [sel.filters]
    })()
    const perFieldFilters = clickedSeriesFilters

    // Fusionner les filtres segment + conditions par champ + filtres existants du chart en un seul AND
    const existingFilter = chartBody.filter
    // Pour un référentiel lié : exclure les lignes sans B (jointure LEFT JOIN → B.idSample NULL).
    // Le widget ne les compte pas (COUNT ignore les NULL), le drill ne doit pas les afficher.
    const linkedNotNullFilters = isLinkedField
      ? [{ fieldUUID: linkedIdSampleKey, operation: "IS NOT NULL" }]
      : []
    const allAdditionalFilters = [...leafFilters, ...perFieldFilters, ...linkedNotNullFilters]
    const drillFilter = (() => {
      if (allAdditionalFilters.length === 0 && !existingFilter) return undefined
      if (allAdditionalFilters.length === 0) return existingFilter
      if (!existingFilter) return { operation: "and", filters: allAdditionalFilters }
      if (existingFilter.operation?.toLowerCase() === "and" && Array.isArray(existingFilter.filters))
        return { ...existingFilter, filters: [...existingFilter.filters, ...allAdditionalFilters] }
      return { operation: "and", filters: [existingFilter, ...allAdditionalFilters] }
    })()

    // Colonnes drill-down = champs de la fiche (propertiesField non vide dans le schéma UX).
    // • Référentiel direct (dashboard) : on exclut les champs loaded_* pour éviter la
    //   multiplication de lignes via GROUP BY. 1 ligne par sample du référentiel du dashboard.
    // • Référentiel lié : on garde uniquement les champs du préfixe loaded_ correspondant
    //   au référentiel cible + son idSample propre pour la navigation vers la fiche.
    const seenUUIDs = new Set()
    const ficheFields = isLinkedField
      ? // Référentiel lié : tous les champs du préfixe cible, sans filtre propertiesField
        // (le formulaire UX du référentiel lié peut ne pas être configuré).
        // On exclut seulement les champs système (_*) et idSample (ajouté séparément).
        (fieldsData ?? [])
          .filter(f => String(f.uuid ?? "").startsWith(linkedPrefix + "."))
          .filter(f => !String(f.uuid ?? "").endsWith(".idSample") && !String(f.uuid ?? "").startsWith("_"))
      : // Référentiel direct : uniquement les champs de la fiche (propertiesField non vide).
        (fieldsData ?? [])
          .filter(f => f.propertiesField && Object.keys(f.propertiesField).length > 0)
          .filter(f => !String(f.uuid ?? "").startsWith("loaded_"))
    // Identifiants techniques à ne JAMAIS afficher (`occurenceId`, `evaluationRunId`…).
    //
    // ⚠️ Il ne suffit pas de les reléguer en fin de classement : un compteur déclare volontiers
    // `occurenceId` dans ses données, ce qui le plaçait en TÊTE de la fenêtre visible — la bande
    // « technique » ne protège que ce qui vient du formulaire. On les écarte donc de la sélection.
    // `idSample` et les `loaded_*.idSample` ne sont pas concernés : ils servent aux jointures et à
    // la navigation, et la modale les masque déjà.
    const uuidsTechniques = new Set(
      (fieldsData ?? []).filter(estColonneTechnique).map(f => String(f?.uuid ?? ""))
    )

    const widgetDataUUIDs = (widgetInfo?.data ?? [])
      .map(f => String(f.uuid || f.id || ""))
      .filter(uuid => uuid.length > 0 && !uuidsTechniques.has(uuid))

    // Les champs déclarés par le widget, résolus en objets pour être classés comme les autres.
    //
    // ⚠️ Ils sont INCLUS dans le classement, pas placés devant lui. Les mettre en tête garantissait
    // qu'ils tiennent dans la fenêtre visible — c'était le but d'origine — mais sur une courbe
    // `data[0]` est la MESURE : le taux de conformité s'affichait en première colonne, devant le
    // plan de contrôle. L'inclusion reste acquise (rien n'est perdu), la position suit le métier.
    // À rang égal ils passent devant, l'ordre d'entrée étant préservé par un tri stable.
    const champsWidget = widgetDataUUIDs.map(
      uuid =>
        (fieldsData ?? []).find(f => String(f?.uuid ?? "") === uuid) ?? {
          uuid,
          name: uuid,
          infoField: {},
        }
    )

    // Colonnes du drill-down, CLASSÉES par identité métier — et complétées par les colonnes métier
    // absentes du formulaire (« Jours de retard », le libellé du contrôle lié), qu'aucun
    // reclassement ne pourrait faire apparaître puisqu'elles ne sont jamais demandées à l'ETL.
    //
    // ⚠️ On ne puise dans `fieldsData` que pour un référentiel DIRECT. Sur un référentiel joint, les
    // colonnes d'occurrence ne décrivent pas ce qu'on liste : les y injecter afficherait les
    // métriques de l'occurrence dans une liste de contrôles.
    const colonnesDrillDown = construireColonnesDrillDown(
      [...champsWidget, ...ficheFields],
      isLinkedField ? undefined : fieldsData,
      widgetInfo?.widgetProps?.drillDownColumns
    )
    const drillSelectAdvanced = [
      ...colonnesDrillDown
        .filter(f => !uuidsTechniques.has(String(f?.uuid ?? "")))
        .map(f => ({ fieldUUID: f.uuid, outputUUID: f.uuid, aggFunction: "" }))
        .filter(f => {
          if (seenUUIDs.has(f.fieldUUID)) return false
          seenUUIDs.add(f.fieldUUID)
          return true
        }),
      // Pour un référentiel lié : inclure l'idSample du référentiel cible pour la navigation.
      ...(isLinkedField && !seenUUIDs.has(linkedIdSampleKey)
        ? [{ fieldUUID: linkedIdSampleKey, outputUUID: linkedIdSampleKey, aggFunction: "" }]
        : []),
      // idSample principal toujours présent (clé de ligne dans l'ETL).
      ...(seenUUIDs.has("idSample") ? [] : [{ fieldUUID: "idSample", outputUUID: "idSample", aggFunction: "" }]),
      // idSample des référentiels liés mixtes : nécessaire pour que l'ETL puisse joindre et
      // grouper par loaded_*.idSample (groupByFieldUUIDs). Placé après idSample pour rester
      // hors des 8 colonnes visibles du DrillDownModal (pas affiché, mais requis pour le GROUP BY).
      // ⚠️ La source est l'UNION des champs du widget et des colonnes reclassées : le libellé du
      // contrôle est un champ `loaded_*` que le reclassement ajoute. Son `idSample` doit être
      // SÉLECTIONNÉ ici, pour deux raisons distinctes — il sert au regroupement quand le widget en
      // déclare un, et c'est lui qui porte la cible de navigation vers la fiche du contrôle.
      // En cible forcée sur la racine, seules les colonnes AFFICHÉES comptent : l'`idSample` de la
      // fiche jointe que le widget compte multiplierait les lignes (une reco par plan).
      ...(!isLinkedField
        ? [...(drillSurRacine ? [] : widgetDataUUIDs), ...colonnesDrillDown.map(f => String(f?.uuid ?? ""))]
            .filter(uuid => uuid.startsWith("loaded_"))
            .map(uuid => {
              const parts = uuid.split(".")
              return parts.length > 1 ? `${parts.slice(0, -1).join(".")}.idSample` : ""
            })
            .filter((idSampleUUID, idx, arr) => idSampleUUID.length > 0 && arr.indexOf(idSampleUUID) === idx && !seenUUIDs.has(idSampleUUID))
            .map(idSampleUUID => ({ fieldUUID: idSampleUUID, outputUUID: idSampleUUID, aggFunction: "" }))
        : []),
    ]

    // Nettoyer le window : loaded_* dans partitionBy/orderBy peut déclencher
    // une multiplication de lignes dans le drill quand la cible est le référentiel principal.
    // Exception : si isLinkedField = true, le drill cible déjà le référentiel lié → les champs
    // loaded_* dans le partitionBy sont valides et doivent être conservés tels quels
    // (les supprimer changerait la granularité de dédup, ex: 25 Contrôles → 13 PDCs).
    const chartWindow = chartBody.window
    const drillWindow = chartWindow
      ? (() => {
          const partitionBy = isLinkedField
            ? (chartWindow.partitionBy ?? [])
            : (chartWindow.partitionBy ?? []).filter(
                c => !String(c.uuid ?? "").startsWith("loaded_")
              )
          const orderBy = isLinkedField
            ? (chartWindow.orderBy ?? [])
            : (chartWindow.orderBy ?? []).filter(
                o => !String(o.uuid ?? "").startsWith("loaded_")
              )
          const hadPartitionBy = (chartWindow.partitionBy ?? []).length > 0
          const effectivePartitionBy =
            hadPartitionBy && !partitionBy.length ? [{ uuid: "idSample" }] : partitionBy
          if (!effectivePartitionBy.length && !orderBy.length) return undefined
          return {
            ...(effectivePartitionBy.length && { partitionBy: effectivePartitionBy }),
            ...(orderBy.length && { orderBy }),
          }
        })()
      : undefined

    // Granularité du drill : doit correspondre à ce que le widget a compté.
    //
    // Règle : si le GROUPER PAR contient au moins un champ de B (loaded_*), chaque barre
    // représente des B distincts → GROUP BY [B.idSample] (dédup sur B).
    // Sinon (GROUPER PAR sur A, ou pas de GROUPER PAR), chaque barre représente des paires A×B
    // → GROUP BY [B.idSample, A.idSample] pour que le count drill = count widget.
    //
    // Exception : COMPTE DISTINGUER → le widget déduplique toujours sur B → GROUP BY [B.idSample].
    const isCountDistinct = widgetInfo?.data?.[0]?.distinct === true
    const groupByHasLinkedField = (widgetInfo?.groupBy ?? []).some(gb =>
      String(gb.uuid || gb.id || "").startsWith("loaded_")
    )
    // Préfixes loaded_* des colonnes liées ajoutées au DrillDown (cas mixte).
    // ⚠️ Pas en cible forcée sur la racine : regrouper par la fiche jointe y répéterait chaque
    // fiche racine autant de fois qu'elle a de fiches jointes (une reco par plan).
    const mixedLinkedPrefixes = !isLinkedField && !drillSurRacine
      ? [
          ...new Set(
            (widgetInfo?.data ?? [])
              .map(f => String(f.uuid || f.id || ""))
              .filter(uuid => uuid.startsWith("loaded_"))
              .map(uuid => {
                const parts = uuid.split(".")
                return parts.length > 1 ? parts.slice(0, -1).join(".") : ""
              })
              .filter(p => p.length > 0)
          ),
        ]
      : []
    const etlBody = {
      selectAdvanced: drillSelectAdvanced,
      groupByFieldUUIDs: isLinkedField
        ? isCountDistinct || groupByHasLinkedField || isGantt
          ? [linkedIdSampleKey]
          : [linkedIdSampleKey, "idSample"]
        : mixedLinkedPrefixes.length > 0
          ? [...mixedLinkedPrefixes.map(p => `${p}.idSample`), "idSample"]
          : [],
      ...(drillFilter && { filter: drillFilter }),
      ...(drillWindow && { window: drillWindow }),
    }

    // Sur une liste d'OCCURRENCES, le clic mène au CONTRÔLE évalué, pas à l'occurrence.
    //
    // Une occurrence est un passage d'évaluation : sa fiche n'apprend rien à qui vient de cliquer
    // sur « 12 évaluations non conformes ». `referentialId` et `idSampleKey` ne servent QU'À la
    // navigation dans la modale — la liste reste pilotée par `etlBody` — donc rien de ce qui
    // s'affiche ne change. Sans colonne de contrôle sélectionnée, on garde la cible d'origine.
    // La colonne de rôle qui identifie la jointure est cherchée dans tout le cube : le client peut
    // afficher « Code » + « Libellé » sans `Code — Libellé`, la cible reste la même jointure.
    const cibleControle = isLinkedField ? null : cibleNavigationControle(colonnesDrillDown, fieldsData)

    setDrillDownState({
      open: true,
      referentialId: cibleControle?.referentialId ?? referentialId,
      dashboardId: idPage,
      etlBody,
      fieldsData: fieldsData ?? [],
      idSampleKey: cibleControle?.idSampleKey ?? linkedIdSampleKey,
      // En-têtes renommés pour CE widget. C'est un libellé d'affichage : la colonne, les filtres
      // et les autres widgets ne bougent pas — ce qui évite d'avoir à renommer une colonne du
      // référentiel pour changer un mot dans un tableau.
      renommagesColonnes: widgetInfo?.widgetProps?.drillDownColumnLabels,
    })
  }

  const getComponentToShow = widgetInfo => {
    if (!widgetInfo) return <div>Error widget Info</div>
    var widgetInfoToUse = JSON.parse(JSON.stringify(widgetInfo))
    var widgetIsCompared = widgetIsComparedFunc(comparisonValues, widgetInfo)
    if (widgetIsCompared && comparedWidget && Object.keys(comparedWidget).length >= 1) {
      widgetInfoToUse = comparedWidget
    }

    if (widgetInfoToUse) {
      const widgetId = widgetInfoToUse?._id?.$oid
      const isFilter = widgetInfoToUse.widgetCategory === widgetCategory.FILTER
      const isRedirectButton = widgetInfoToUse?.widgetType === "redirectButton"
      const isFullScreened =
        (!isFilter || !isRedirectButton) &&
        !showWidgetConfigButton &&
        !isWidgetFromFullScreen &&
        widgetInfoToUse?.widgetProps?.hasOwnProperty("isFullScreenable") &&
        widgetInfoToUse?.widgetProps?.isFullScreenable
      const showTitle =
        !isWidgetFromFullScreen &&
        (widgetInfoToUse.title ||
          (widgetInfoToUse?.widgetProps?.hasOwnProperty("disableTitle") &&
            !widgetInfoToUse?.widgetProps?.disableTitle))
      const WidgetComponent = getWidgetComponent(widgetInfoToUse.widgetType)
      const widgetTraduction = getWidgetTraduction(widgetInfoToUse.widgetType, languageId)

      var dataArgument = {
        valueFilterSelected: eventFilters,
        specificWidgetFilterData: specificWidgetFilterData,
        specificWidgetData: specificWidgetData,
        comparisonValues: comparisonValues,
        valueData: valueData,
        categoryData: categoryData,
      }

      var filterActions = {
        onSelectFilterValueWrapperFunc,
        onRemoveSelectedValueFunc,
        onSelectComparisonValueWrapperFunc,
        onRemoveComparisonValue,
      }

      var othersActions = {
        updateDataSource: updateDataSource,
        savePipelineModifications: savePipelineModifications,
      }

      var dataOptions = { languageId }

      const widgetHasNoData = isEmptyWidget(widgetInfoToUse) && showWidgetConfigButton

      const paramsGetDataRelatedProps = {
        widgetInfo: widgetInfoToUse,
        dataArgument,
        filterActions,
        othersActions,
        fieldsData,
        partialTrad,
        dataOptions,
        widgetHasNoData,
      }

      var dataRelatedProps = getDataRelatedProps(paramsGetDataRelatedProps)
      var newWidgetPropsToUpdate = getNewWidgetPropsToInjectInComp(
        widgetInfoToUse,
        comparisonValues,
        fieldsData,
        businessId,
        token
      )
      var newWidgetProps = {
        ...widgetInfoToUse.widgetProps,
        ...newWidgetPropsToUpdate,
      }
      widgetInfoToUse.widgetProps = newWidgetProps

      const cogMargin = isFilter || isRedirectButton ? "0px 0px 0px 0px" : "0px 20px 0px 0px"
      const cogPadding = isFilter || isRedirectButton ? "0px" : "10px"
      const bgColor =
        isFilter || isRedirectButton ? "transparent" : widgetInfoToUse?.widgetProps?.backgroundColor
      const activeBackground =
        isFilter || isRedirectButton ? true : widgetInfoToUse?.widgetProps?.activeBackground
      const bgColorProps = {
        backgroundColor: bgColor,
        activeBackground: activeBackground,
      }
      const chartType = widgetInfoToUse?.chartType

      return (
        <WidgetContainer
          id="widgetContainer"
          key={widgetId}
          {...bgColorProps}
          borderRadius={widgetInfoToUse?.widgetProps?.borderRadius}
          borderStyle={widgetInfoToUse?.widgetProps?.borderStyle}
          borderColor={widgetInfoToUse?.widgetProps?.borderColor}
          borderSize={widgetInfoToUse?.widgetProps?.borderSize}
          activeRadius={widgetInfoToUse?.widgetProps?.activeRadius}
          activeBorder={widgetInfoToUse?.widgetProps?.activeBorder}
        >
          <WidgetHeader
            showTitle={showTitle}
            role="widgetHeader"
            showWidgetConfigButton={showWidgetConfigButton}
          >
            {showTitle && (
              <Title
                labelColor={widgetInfoToUse?.widgetProps?.titleColor}
                labelSize={widgetInfoToUse?.widgetProps?.titleSize}
                labelWeight={widgetInfoToUse?.widgetProps?.titleWeight}
                titleAlignement={widgetInfoToUse?.widgetProps?.titleAlignement}
                showWidgetConfigButton={showWidgetConfigButton}
              >
                {widgetInfoToUse.title}
              </Title>
            )}
            {showTitle && <WidgetDescription description={widgetInfoToUse?.widgetProps?.description} />}
            {!widgetHasNoData && (
              <ButtonSettings
                style={isFilter ? { padding: "3.5px 0" } : {}}
                onMouseDown={e => e.stopPropagation()}
                primaryActionColor={primaryActionColor}
                showTitle={showTitle}
              >
                {isDataLoaderVisible &&
                  !isFilter &&
                  (!isDataLoaded ? (
                    <div className="loading_container">
                      {" "}
                      <i className="fas fa-spinner loading" />{" "}
                    </div>
                  ) : (
                    <div className="loading_container">
                      {" "}
                      <i className="fas fa-check success" />{" "}
                    </div>
                  ))}
                {isFullScreened && (
                  <Button
                    color={primaryActionColor}
                    onClick={
                      !isAWidgetFromSettings
                        ? () => onWidgetFullScreenClick(widgetInfoToUse?._id?.$oid)
                        : () => {}
                    }
                    backgroundColor="white"
                    borderColor={primaryActionColor}
                    icon={<IconFullScreenOn fill={primaryActionColor} />}
                    style={{
                      padding: "10px",
                      marginRight: "20px",
                      height: "30px",
                      width: "30px",
                    }}
                  />
                )}
                {showWidgetConfigButton && (
                  <Button
                    color={primaryActionColor}
                    onClick={() => onWidgetConfigClick(widgetInfoToUse?._id?.$oid)}
                    backgroundColor="white"
                    borderColor={primaryActionColor}
                    icon={<i className="fas fa-cog" />}
                    style={{
                      margin: cogMargin,
                      padding: cogPadding,
                      height: "30px",
                      width: "30px",
                    }}
                  />
                )}
              </ButtonSettings>
            )}
          </WidgetHeader>

          <ChartContainer
            id={`chartContainer${widgetId}`}
            showConfig={!widgetHasNoData ? showWidgetConfigButton : false}
            isFilter={isFilter}
            chartType={chartType}
            isRedirectButton={isRedirectButton}
            decreaseSizeForTitle={!!showTitle}
          >
            {widgetHasNoData && (
              <ConfigurationContainer>
                <div onMouseDown={e => e.stopPropagation()}>
                  <Button
                    color="white"
                    backgroundColor={primaryActionColor}
                    borderColor={primaryActionColor}
                    colorHover={primaryActionColor}
                    backgroundColorHover="white"
                    icon={<i className="fas fa-cog" />}
                    text={trad?.CONFIGURE}
                    onClick={() => onWidgetConfigClick(widgetInfoToUse?._id?.$oid)}
                  />
                </div>
              </ConfigurationContainer>
            )}

            <ErrorBoundary errorComponent={<ErrorWidgetComponent />}>
              <WidgetComponent
                {...widgetInfoToUse.widgetProps}
                {...dataRelatedProps}
                {...(widgetInfoToUse.widgetType === "hierarchicalFilter" && {
                  allowedIds: hierarchyAllowedIds,
                })}
                {...(drillDownEnabled !== false &&
                  TYPES_AVEC_DRILL_DOWN.includes(widgetInfoToUse.widgetType) && {
                  onSegmentClick: handleDrillDown,
                })}
                languageId={languageId}
                isAWidgetFromSettings={isAWidgetFromSettings}
                fieldsData={fieldsData}
                widgetTitle={widgetInfoToUse.title}
                primaryActionColor={primaryActionColor}
                widgetTraduction={widgetTraduction}
                sideMenuColor={sideMenuColor}
                layout={layout}
                currentColsNumber={currentColsNumber}
                breakpoint={breakpoint}
                configMode={showWidgetConfigButton}
                widgetGroupBy={widgetInfoToUse?.groupBy}
                requestLoad={widgetInfoToUse?.request?.selectAdvanced}
                rssUrl={widgetInfoToUse?.rssUrl || ""}
              />
            </ErrorBoundary>
          </ChartContainer>
        </WidgetContainer>
      )
    }
    return <div>Widget Not FOUND</div>
  }

  const renderWidget = () => {
    const isWidgetLoadingInVisualisation =
      isLoadingWidget && !showWidgetConfigButton && widgetInfo?.widgetType !== "googleMap"
    const isWidgetEmptyAndNotInConfiguration =
      isEmptyWidget(widgetInfo) && !showWidgetConfigButton && !isAWidgetFromSettings

    if (isWidgetLoadingInVisualisation) return <WidgetLoader />

    if (isWidgetEmptyAndNotInConfiguration && widgetInfo.chartType !== "googleMap")
      return (
        <EmptyWidgetContainer key={widgetInfo?._id?.$oid}>
          {trad?.WIDGET_NOT_CONFIGURED}
        </EmptyWidgetContainer>
      )

    return getComponentToShow(widgetInfo)
  }

  const ErrorWidgetComponent = () => (
    <ErrorWidgetContainer>
      <ErrorWidget />
      {trad?.OUPS_AN_ERROR_OCCURED}
    </ErrorWidgetContainer>
  )

  return (
    <>
      {renderWidget()}
      {drillDownState.open && (
        <DrillDownModal
          onClose={() => setDrillDownState(s => ({ ...s, open: false }))}
          referentialId={drillDownState.referentialId}
          dashboardId={drillDownState.dashboardId}
          etlBody={drillDownState.etlBody}
          fieldsData={drillDownState.fieldsData}
          idSampleKey={drillDownState.idSampleKey}
          renommagesColonnes={drillDownState.renommagesColonnes}
          title={widgetInfo?.title}
          preloadedItems={drillDownState.preloadedItems}
          preloadedColumns={drillDownState.preloadedColumns ?? undefined}
        />
      )}
    </>
  )
}

Widget.propTypes = {
  userInformation: PropTypes.object,
  primaryActionColor: PropTypes.string,
  onWidgetConfigClick: PropTypes.func,
  isAWidgetFromSettings: PropTypes.bool,
  sideMenuColor: PropTypes.string,
}

Widget.defaultProps = {
  userInformation: {},
  primaryActionColor: "#01014B",
  onWidgetConfigClick: () => {},
  isAWidgetFromSettings: false,
  isWidgetFromFullScreen: false,
  sideMenuColor: "",
  specificWidgetFilterData: {},
}

const mapDispatchToProps = dispatch => ({
  onSelectComparisonValue: core => dispatch(selectComparisonValueAction(core)),
  onRemoveComparisonValue: core => dispatch(removeComparisonValueAction(core)),
  onGetWidgetData: core => dispatch(getWidgetDataAction(core)),
  setRequestStatus: core => dispatch(setWidgetRequestStatus(core)),
})

const mapStateToProps = () =>
  createStructuredSelector({
    primaryActionColor: selectPrimaryActionColor,
    specificWidgetData: makeGetWidgetDataWithId(),
    specificWidgetFilterData: makeGetWidgetFilterDataWithId(),
    specificWidgetRequestStatus: makeGetWidgetRequestStatusWithId(),
    comparisonValues: selectComparisonValues,
    drillDownEnabled: selectDrillDownEnabled,
    dependentFilters: selectDependentFilters,
    // fieldsData: selectDataCatalogueFields,
    languageId: selectLangageID,
    businessId: selectBusinessId,
    token: selectCurrentToken,
  })

export default compose(connect(mapStateToProps, mapDispatchToProps))(withRouter(Widget))
